import TestFilterTabs from '../generic/TestFilterTabs';
import ChapterAnalysisLegend from './ChapterAnalysisLegend';
import ChapterAnalysisContent from './ChapterAnalysisContent';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useChapterAnalysis } from './hooks/useChapterAnalysis';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import PackPagination from '../generic/PackPagination';
import { useDispatch, useSelector } from 'react-redux';
import PackButton from '../generic/PackButton';

const ChapterAnalysis = ({ packId, onResetFilters }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.pack);
  const { data, loading, error, page, setPage } = useChapterAnalysis(packId, filters?.testFilter);
  const navigate = useNavigate();

  // Scroll states for hide-on-scroll behavior
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(true);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const currentScrollY = scrollContainerRef.current.scrollTop;
      const isTabletOrBelow = window.innerWidth < 1024;

      if (!isTabletOrBelow) {
        if (currentScrollY <= 0) {
          if (!isDescriptionVisible) setIsDescriptionVisible(true);
        } else if (currentScrollY > 70 && isDescriptionVisible) {
          setIsDescriptionVisible(false);
        } else if (currentScrollY < 30 && !isDescriptionVisible) {
          setIsDescriptionVisible(true);
        }
        setIsHeaderVisible(true);
        return;
      }

      if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY]);

  const testId = useMemo(() => {
    if (!data) return null;

    for (const subject of data || []) {
      const chapters = subject.chapters || {};
      for (const chapterName in chapters) {
        const chapter = chapters[chapterName];
        for (const topicName in chapter.topics) {
          const topic = chapter.topics[topicName];
          if (topic.topicUnanswered && topic.topicUnanswered.length > 0) {
            return topic.topicUnanswered[0].testId;
          }
          if (topic.topicIncorrect && topic.topicIncorrect.length > 0) {
            return topic.topicIncorrect[0].testId;
          }
          if (topic.topicCorrect && topic.topicCorrect.length > 0) {
            return topic.topicCorrect[0].testId;
          }
        }
      }
    }
    return null;
  }, [data]);

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col h-screen overflow-y-auto" ref={scrollContainerRef}>
      {/* Sticky Blurred Header */}
      <div
        className={`z-10 q3-glass rounded transition-transform duration-300 ease-in-out lg:transform-none ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="p-6 pb-4">
          {/* Mobile Back Button */}
          <PackButton navigateTo="/v2/home" />

          <div className="mb-6">
            <h1 className="text-3xl font-bold text-[var(--q3-neutral-default)] mb-2">Chapter Analysis</h1>
          </div>

          <TestFilterTabs />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="p-6 pt-2 space-y-8">
        <ChapterAnalysisLegend />
        <ChapterAnalysisContent
          data={data}
          loading={loading}
          error={error}
          packId={packId}
          testId={testId}
          onResetFilters={onResetFilters}
        />

        {data?.totalPagesAvailable > 1 && (
          <PackPagination
            totalPages={data?.totalPagesAvailable}
            activePage={page}
            onPageClick={(newPage) => setPage(newPage)}
          />
        )}
      </div>
    </div>
  );
};

export default ChapterAnalysis;
